import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';

import { CallCalling } from '@yandex-360/iconostasis/dist/icons/CallCalling';
import { PeerType } from '@yandex-360/telemost-sdk/models';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../init-store';
import { VIRTUAL_CALLEE } from '../../../pages/call/constants';
import { getCallee, getParticipant } from '../../../pages/call/selectors';
import { TrackState } from '../../../pages/call/types';
import { SELF } from '../../constants';
import { selectIsTestplane } from '../../features/flags/selectors';
import {
    AvatarPlaceholder,
    getAvatarPlaceholder,
    getFontSizeByWidth,
    getFontWeightByWidth,
} from '../../helpers/avatar-placeholder';
import { browser } from '../../helpers/browser-capabilities';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import SvgImage from '../svg-image';
import css from './index.css';

interface Props {
    participantId: string;
    placeholderStyle?: CSSProperties;
    className?: string;
    uid?: string;
    width?: number;
    height?: number;
}

interface ParticipantDataForIcon {
    uid: Maybe<string>;
    videoTrackState: Maybe<TrackState>;
    shouldRenderAvatar: boolean;
    avatarUrl: Maybe<string>;
    avatarPlaceholder?: AvatarPlaceholder;
    peerType?: PeerType;
}

const getParticipantDataForIcon = (state: RootState, id: string): ParticipantDataForIcon => {
    if (id === VIRTUAL_CALLEE) {
        const callee = getCallee(state);

        return {
            uid: undefined,
            videoTrackState: undefined,
            avatarPlaceholder: callee?.avatarPlaceholder || getAvatarPlaceholder(),
            shouldRenderAvatar: Boolean(callee?.shouldRenderAvatar),
            avatarUrl: callee?.avatarUrl,
        };
    }

    const waitingRoomParticipant = state.call.waitingRooms.waitingRoomPeers.find((peer) => peer.id === id);
    if (waitingRoomParticipant) {
        return {
            uid: waitingRoomParticipant.uid,
            videoTrackState: undefined,
            shouldRenderAvatar: Boolean(waitingRoomParticipant.avatarUrl) && !waitingRoomParticipant.isDefaultAvatar,
            avatarUrl: waitingRoomParticipant.avatarUrl,
            avatarPlaceholder: waitingRoomParticipant.avatarPlaceholder,
            peerType: waitingRoomParticipant.peerType,
        };
    }
    const participant = getParticipant(state, id);
    const { user } = state;
    const isSelf = id === SELF;

    return {
        uid: isSelf ? user?.uid : participant?.uid,
        videoTrackState: participant?.videoTrackState,
        shouldRenderAvatar: Boolean(
            user && isSelf ? user.avatarUrl && !user.isDefaultAvatar : participant.shouldRenderAvatar
        ),
        avatarUrl: user && isSelf ? user.avatarUrl : participant.avatarUrl,
        avatarPlaceholder: user && isSelf ? user.avatarPlaceholder : participant.avatarPlaceholder,
        peerType: participant.peerType,
    };
};

const ParticipantIcon: FC<Props> = ({
    className,
    participantId: id,
    placeholderStyle = {},
    uid: participantUid,
    width,
    height,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const style: CSSProperties = {
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : '100%',
        aspectRatio: '1',
        ...placeholderStyle,
    };

    const { uid, videoTrackState, shouldRenderAvatar, avatarUrl, avatarPlaceholder, peerType } = useTypedSelector(
        (state) => getParticipantDataForIcon(state, id)
    );
    const isTestplane = useSelector(selectIsTestplane);

    const isGuest = !participantUid && !uid && id !== VIRTUAL_CALLEE;
    const isVideoOn = Boolean(videoTrackState && !videoTrackState.muted);
    const isSmall = !browser()?.isFirefox() && isVideoOn;

    const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);

    useEffect(() => {
        if (avatarUrl) {
            const img = new Image();

            img.src = avatarUrl;
            img.onload = () => setIsAvatarLoaded(true);
        }
    }, [avatarUrl]);

    const shouldRenderLoader = !avatarPlaceholder || (shouldRenderAvatar && !isAvatarLoaded);

    if (shouldRenderAvatar && isAvatarLoaded) {
        return (
            <div
                className={cn(css.ParticipantIcon, className)}
                style={{
                    backgroundImage: `url(${avatarUrl})`,
                    ...style,
                }}
            />
        );
    }

    //Проверяем, что к нам пришла не Гермиона. Это нужно для большей стабильности тестов:
    // из-за анимации, может оказаться сложно поймать нужный момент для взятия скриншота
    if (shouldRenderLoader) {
        return !isTestplane ? (
            <div className={cn(css.ParticipantIcon, className, css.pulse)} style={{ ...style }} />
        ) : null;
    }

    const containerWidth = containerRef?.current?.getBoundingClientRect?.()?.width;
    const fontSize = getFontSizeByWidth(Number(width ?? containerWidth));
    const fontWeight = getFontWeightByWidth(Number(width ?? containerWidth));

    style.backgroundColor = avatarPlaceholder.backgroundColor;
    style.color = avatarPlaceholder.textColor;
    style.fontSize = `${fontSize}px`;

    if (fontWeight) {
        style.fontWeight = fontWeight;
    }

    return (
        <div
            style={style}
            className={cn(css.Placeholder_image, css.ParticipantIcon, className, {
                [css.Placeholder_small]: isSmall,
            })}
            ref={containerRef}
        >
            {peerType === PeerType.INCOMING_PHONE_CALL ? (
                <div className={css.callIconContainer}>
                    <CallCalling className={css.callIcon} />
                </div>
            ) : isGuest ? (
                <SvgImage imageName="guestIcon" fill={avatarPlaceholder.textColor} />
            ) : (
                <div className={css.Placeholder_abbreviationText}>{avatarPlaceholder.abbreviation}</div>
            )}
        </div>
    );
};

export default ParticipantIcon;
